/**
 * Per-user card order for the Safety Management hub (/safety).
 *
 * The hub's sections and cards are static data in pages/SafetyHub.js; a user
 * may drag cards into their own order WITHIN a section. The order is saved
 * through the existing per-user dashboard layout store
 * (PUT /api/dashboard/v2/layout, dashboard_key 'safety-hub') as a flat list of
 * `{ id }` entries, where `id` is the card's stable `key`.
 *
 * Section boundaries are enforced HERE, not by the saved data: a card always
 * stays in the section that declares it, whatever the saved list contains.
 * The saved list only ever decides the order of cards inside their own section.
 *
 * Merge rule for a saved order (deterministic):
 *   1. Cards whose key is in the saved list are placed in saved order.
 *   2. A card missing from the saved list (e.g. a card added to the hub after
 *      the user saved) keeps its DEFAULT relative position: it is inserted
 *      directly after the card that precedes it in the default order, or at the
 *      front of the section when it is the section's first default card. Cards
 *      are processed in default order, so several new cards in a row stay
 *      together in their default sequence.
 *   3. Unknown ids, and ids of cards that live in a different section, are
 *      ignored. A repeated id counts only at its first position.
 *
 * Visibility (`show`) is not considered here: ordering is applied to every
 * card, and the page filters to visible cards afterwards. That way a card the
 * user currently cannot see keeps its saved slot if access is granted later.
 */

/** dashboard_key under which the hub order is stored. */
export const SAFETY_HUB_LAYOUT_KEY = 'safety-hub';

/**
 * Card ids from a saved layout (`[{ id }, ...]` as returned by getLayout).
 * Anything malformed is dropped; a missing layout yields an empty list.
 *
 * @param {Array|null|undefined} layout
 * @returns {string[]}
 */
export function idsFromLayout(layout) {
  if (!Array.isArray(layout)) return [];
  return layout
    .map((entry) => (entry && typeof entry.id === 'string' ? entry.id : null))
    .filter(Boolean);
}

/**
 * Sections with each section's cards reordered by the saved ids.
 * Returns new section/card arrays; the input is not mutated.
 *
 * @param {Array<{id: string, cards: Array<{key: string}>}>} sections default order
 * @param {string[]} savedIds flat list of card keys, in saved order
 * @returns {Array} sections in the user's order
 */
export function applySavedOrder(sections, savedIds) {
  const rank = new Map();
  (Array.isArray(savedIds) ? savedIds : []).forEach((id, i) => {
    if (!rank.has(id)) rank.set(id, i);
  });
  if (rank.size === 0) return sections.map((s) => ({ ...s, cards: [...s.cards] }));

  return sections.map((section) => {
    const saved = section.cards
      .filter((c) => rank.has(c.key))
      .sort((a, b) => rank.get(a.key) - rank.get(b.key));

    // Insert each unsaved card after its default-order predecessor.
    const ordered = [...saved];
    section.cards.forEach((card, i) => {
      if (rank.has(card.key)) return;
      const prev = i > 0 ? section.cards[i - 1] : null;
      const at = prev ? ordered.indexOf(prev) + 1 : 0;
      ordered.splice(at, 0, card);
    });

    return { ...section, cards: ordered };
  });
}

/**
 * Flat layout payload for saveLayout: every card key, section by section.
 *
 * @param {Array<{cards: Array<{key: string}>}>} sections in the user's order
 * @returns {Array<{id: string}>}
 */
export function buildLayoutPayload(sections) {
  return sections.flatMap((s) => s.cards.map((c) => ({ id: c.key })));
}

/**
 * Move one card onto another's position inside the SAME section.
 * Returns the input unchanged when either key is unknown, the two cards are in
 * different sections, or the card is dropped on itself.
 *
 * Works on the full card list (including cards hidden from this user), so a
 * hidden card keeps its slot; the visible cards end up in the same order as an
 * arrayMove over the visible cards alone.
 *
 * @param {Array} sections current order
 * @param {string} activeKey the dragged card
 * @param {string} overKey the card it was dropped on
 * @returns {Array} sections in the new order
 */
export function moveCard(sections, activeKey, overKey) {
  if (!activeKey || !overKey || activeKey === overKey) return sections;
  const section = sections.find((s) => s.cards.some((c) => c.key === activeKey));
  if (!section) return sections;
  const from = section.cards.findIndex((c) => c.key === activeKey);
  const to = section.cards.findIndex((c) => c.key === overKey);
  if (to < 0) return sections; // dropped onto a card of another section

  const cards = [...section.cards];
  const [moved] = cards.splice(from, 1);
  cards.splice(to, 0, moved);
  return sections.map((s) => (s === section ? { ...s, cards } : s));
}
